import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import moment from "moment";
// styles
import s from "styles/pages/Pages.module.scss";
// utils
import {
    DetailModal,
    HorarioCSVBatchModal,
    ReportePresentismoModal,
} from "./Actions";
import XGridServer from "components/UI/Grid/XGridServer";

// consts
import { usuariosXServicio as titles } from "consts/titulos-de-tablas";
import { ACTIONS } from "consts/Actions";
import { Routes } from "consts/Routes-App";
//Assets
import {
    Cached,
    EventNote,
    SearchOutlined,
    UploadFile,
} from "@mui/icons-material";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { routeInfo, userCredentials } from "recoilState/GlobalState";
import { GateUsuarioGet } from "services/services-mia/miakey";

import Filters from "components/Filters";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import { useService } from "components/route/ServiceRoute";
import Modal from "components/UI/Modal/Modal";
import { Button, Buttons, HeaderButton } from "components";

const ACCESO_FILTER_OPTIONS = [
    { cod: "Todos", nombre: "Todos" },
    { cod: "Y", nombre: "Con acceso" },
    { cod: "N", nombre: "Sin acceso" },
];

export default function UsuariosAccesos() {
    const [modalDetails, setModalDetails] = useState({ isOpen: false });
    const [rowSelected, setRowSelected] = useState({ status: false, row: {} });
    const [forceUpload, setForceUpload] = useState(false);

    const { service } = useService();

    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const updateList = useCallback(() => {
        setForceUpload(Date.now());
    }, []);
    const tableTitles = useMemo(() => [
        ...titles,
        { field: "tieneAcceso", headerName: "Tiene Acceso", align: "center" },
    ], []);

    const formik = useFormik({
        initialValues: {
            apellido: "",
            dni: "",
            usuarioTieneAcceso: "Todos",
        },
        onSubmit: () => {
            updateList();
        },
    });

    const resetFilters = () => {
        formik.setFieldValue("apellido", "");
        formik.setFieldValue("dni", "");
        formik.setFieldValue("usuarioTieneAcceso", "Todos");
        return updateList();
    };

    const getUsers = async (page, pageSize) => {
        const { result, total } = await GateUsuarioGet(
            {
                page: page + 1,
                pageSize,
                usuarioNroDeDocumentoX:
                    formik.values.dni === "" ? null : formik.values.dni,
                usuarioApellidos:
                    formik.values.apellido === ""
                        ? null
                        : formik.values.apellido,
                usuarioTieneAcceso:
                    formik.values.usuarioTieneAcceso === "Todos"
                        ? null
                        : formik.values.usuarioTieneAcceso,
            },
            credentials
        );

        const newRows = await result.map((usuario) => {
            return {
                id: usuario.merchantServicioUsuarioId,
                usuarioId: usuario.usuarioId,
                usuarioNombreCompleto: usuario.usuarioNombreCompleto,
                nombreServicio: usuario.servicioNombre,
                servicioId: usuario.servicioId,
                estadoCod: usuario.relacionStatusCod,
                email: usuario.usuarioMail,
                rolId: usuario.rolId,
                rolCod: usuario.rolCod,
                rolNombre: usuario.rolNombre,
                [titles[0].field]: usuario.usuarioNombreCompleto,
                [titles[1].field]: usuario.usuarioNroDeDocumentoX,
                [titles[2].field]: usuario.tipoDeDocumentoCod,
                [titles[3].field]: usuario.usuarioSexo,
                [titles[4].field]: usuario.usuarioFechaDeNacimiento,
                [titles[5].field]: usuario.usuarioMail,
                [titles[6].field]: usuario.rolNombre,
                [titles[7].field]: usuario.usuarioId === null ? "NO" : "SI",
                [titles[8].field]: usuario.usuarioFechaDeAlta
                    ? moment(usuario.usuarioFechaDeAlta)
                        .format("YYYY-MM-DD")
                        .toString()
                    : null,
                [titles[9].field]: usuario.relacionStatus,
                [titles[10].field]: usuario.usuarioFecha,
                [tableTitles[11].field]:
                    usuario.tieneAcceso === "Y" ? "SI" : "NO",
            };
        });
        return {
            rows: newRows,
            rowCount: total,
        };
    };

    useEffect(() => {
        setRoute(Routes.usuariosAccesos);
    }, [setRoute]);

    useEffect(() => {
        updateList();
    }, [service, updateList]);

    const handleClick = ({ procede, title }) => {
        setModalDetails({ procede, title, isOpen: true });
    };

    const closeModal = () => {
        if (modalDetails.procede === ACTIONS.BATCH) {
            updateList();
        }
        setModalDetails({ isOpen: false });
    };

    return (
        <main className={s.main}>
            <Modal
                open={modalDetails.isOpen}
                title={modalDetails.title}
                onClose={closeModal}
                wide={modalDetails.procede === ACTIONS.ASISTENCIA}
            >
                {modalDetails.procede === ACTIONS.DETAIL && (
                    <DetailModal
                        usuario={rowSelected?.row}
                        service={service}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.BATCH && (
                    <HorarioCSVBatchModal
                        isOpen={modalDetails.isOpen}
                        exportLabel={service?.servicioNombre}
                        updateList={updateList}
                        closeModal={closeModal}
                    />
                )}
                {modalDetails.procede === ACTIONS.ASISTENCIA && (
                    <ReportePresentismoModal
                        key={`${rowSelected?.row?.id ?? ""}-${rowSelected?.row?.usuarioId ?? ""
                            }`}
                        usuario={rowSelected?.row}
                        closeModal={closeModal}
                        open={modalDetails.isOpen}
                    />
                )}
            </Modal>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            {
                                type: "text",
                                name: "apellido",
                                label: "Apellido",
                                disabled: false,
                            },
                            {
                                type: "text",
                                name: "dni",
                                label: "Documento",
                                disabled: false,
                            },
                            {
                                type: "select",
                                name: "usuarioTieneAcceso",
                                label: "Tiene acceso",
                                disabled: false,
                                options: ACCESO_FILTER_OPTIONS,
                                firstOptionKey: "cod",
                                firstOptionName: "nombre",
                                firstOptionValue: "cod",
                            },
                        ]}
                    />
                    <HeaderButton
                        text={"Limpiar filtros"}
                        onClick={() => resetFilters()}
                    >
                        <FilterAltOffRoundedIcon />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Actualizar"}
                        onClick={updateList}
                        disabled={rowSelected.row.id}
                    >
                        <Cached />
                    </HeaderButton>
                    <HeaderButton
                        text={"Ver detalle"}
                        handleClick={handleClick}
                        data={{
                            title: `Detalle de Accesos - ${rowSelected.row[titles[0].field] ?? ""
                                }`,
                            procede: ACTIONS.DETAIL,
                        }}
                        disabled={!rowSelected.row.id}
                    >
                        <SearchOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Reporte de presentismo"}
                        handleClick={handleClick}
                        data={{
                            title: `Reporte de presentismo - ${rowSelected.row[titles[0].field] ?? ""
                                }`,
                            procede: ACTIONS.ASISTENCIA,
                        }}
                        disabled={
                            !rowSelected.row.id ||
                            rowSelected.row.usuarioId == null
                        }
                    >
                        <EventNote />
                    </HeaderButton>
                    <HeaderButton
                        text={"Importar horarios CSV"}
                        handleClick={handleClick}
                        data={{
                            title: "Importar Asignación de Horarios",
                            procede: ACTIONS.BATCH,
                        }}
                        disabled={
                            rowSelected.row.id ||
                            service.servicioPermiso !== "E"
                        }
                    >
                        <UploadFile />
                    </HeaderButton>
                </Buttons>
            </div>

            <div className={s.container}>
                <XGridServer
                    titles={tableTitles}
                    updateList={getUsers}
                    forceUpload={forceUpload}
                    secondarySelected={rowSelected}
                    setSecondarySelected={setRowSelected}
                />
            </div>
        </main>
    );
}
